import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, Fields } from 'redux-form';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import _get from 'lodash/get';
import _isString from 'lodash/isString';
import { PREMIUM_CAP_ENTITY, PREMIUM_CAP_RULE } from 'constants/logic';
import { intlShape, reduxFormPropTypes } from 'constants/propTypes';
import Banner from 'components/Banner';
import Button from 'components/Button';
import Confirm from 'components/Confirm';
import { DropdownPillMenu } from 'components/Dropdown';
import { DateControls } from 'components/DateControls';
import Message from 'components/Message';
import {
  Fieldset,
  FieldsetTitle,
  Input,
  RadioButton,
  TimePicker,
  DayOfWeekPicker,
  Autocomplete,
} from 'components/Form';
import { MAP_DAY_ABBR_TO_FULL, MAP_DAY_FULL_TO_ABBR } from '../PremiumCaps.helpers';
import { rulesModalMessages as messages, commonMessages, deleteModalMessages } from '../PremiumCaps.messages';
import './PremiumCapsRuleModal.scss';

export default class PremiumCapModal extends Component {
  static propTypes = {
    ...reduxFormPropTypes.form,
    classes: PropTypes.arrayOf(
      PropTypes.shape({
        classId: PropTypes.number,
        className: PropTypes.string,
      })
    ),
    schedules: PropTypes.arrayOf(
      PropTypes.shape({
        scheduleId: PropTypes.number,
        date: PropTypes.string,
        startTime: PropTypes.string,
      })
    ),
    currentValues: PropTypes.shape({
      ruleEntity: PropTypes.oneOf(Object.keys(PREMIUM_CAP_ENTITY)),
      classId: PropTypes.number,
    }),
    fetchSchedulesForClass: PropTypes.func,
    setField: PropTypes.func,
    deleteRule: PropTypes.func,
    intl: intlShape,
  };

  componentDidUpdate = prevProps => {
    const {
      resizeModal,
      currentValues: { ruleEntity: currentRuleEntity },
    } = this.props;
    const {
      currentValues: { ruleEntity: previousRuleEntity },
    } = prevProps;
    if (currentRuleEntity !== previousRuleEntity) {
      resizeModal();
    }
  };

  renderCheckbox = (input, labelMessage, disabled) => (
    <label htmlFor={input.name}>
      <input {...input} type="checkbox" id={input.name} checked={input.value} disabled={disabled} />
      <Message message={labelMessage} />
    </label>
  );

  renderRuleEntity = ({ input: { onChange }, disabled }) => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
      <Message
        message={messages.label.ruleEntity}
        as={FieldsetTitle}
        className="premium-caps-rule-modal__fieldset-title"
      />
      <div className="premium-caps-rule-modal__radio-container">
        <Field
          name="ruleEntity"
          value={PREMIUM_CAP_ENTITY.SCHEDULE}
          type="radio"
          component={RadioButton}
          onChange={() => onChange(null)}
          disabled={disabled}
          inline>
          <Message message={commonMessages.scheduleRule} />
        </Field>
        <Field
          name="ruleEntity"
          value={PREMIUM_CAP_ENTITY.CLASS}
          type="radio"
          component={RadioButton}
          onChange={() => onChange(null)}
          disabled={disabled}
          inline>
          <Message message={commonMessages.classRule} />
        </Field>
        <Field
          name="ruleEntity"
          value={PREMIUM_CAP_ENTITY.VENUE}
          type="radio"
          component={RadioButton}
          onChange={() => onChange(null)}
          disabled={disabled}
          inline>
          <Message message={commonMessages.venueRule} />
        </Field>
      </div>
    </Fieldset>
  );

  renderRuleLevelCheckbox = ({ input, labelMessage, disabled }) => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1 mt--">
      {this.renderCheckbox(input, labelMessage, disabled)}
    </Fieldset>
  );

  renderClassId = ({ input: { onChange, ...restInput }, classes, fetchSchedulesForClass, ...restProps }) => {
    const options = classes.map(({ classId, className }) => ({
      label: `${classId} - ${className}`,
      value: classId,
    }));

    return (
      <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
        <Message
          message={messages.label.class}
          as={FieldsetTitle}
          className="premium-caps-rule-modal__fieldset-title"
        />
        <Autocomplete
          {...restProps}
          input={{
            ...restInput,
            onChange: classId => {
              onChange(classId);
              fetchSchedulesForClass(classId);
            },
          }}
          options={options}
          placeholder={messages.placeholder.classPicker}
        />
      </Fieldset>
    );
  };

  renderScheduleId = ({ input: { value, onChange }, classId, schedules }) => {
    const { setField, intl } = this.props;

    return (
      <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
        <Message
          message={messages.label.schedule}
          as={FieldsetTitle}
          className="premium-caps-rule-modal__fieldset-title"
        />
        {!classId ? (
          <p>-</p>
        ) : (
          <DropdownPillMenu
            items={schedules}
            getKey={({ scheduleId }) => scheduleId}
            getText={({ scheduleId, date, startTime } = {}) =>
              !scheduleId
                ? intl.formatMessage(messages.placeholder.schedulePicker)
                : `${scheduleId} - ${intl.formatDate(`${date} ${startTime}`, { format: 'long-w-dow-time' })}`
            }
            isSelected={({ scheduleId }) => scheduleId === value}
            onSelect={({ scheduleId }) => {
              const { date, startTime, endTime } = schedules.find(s => s.scheduleId === scheduleId);
              onChange(scheduleId);
              setField('entityMetadata.startDate', date);
              setField('entityMetadata.startTime', startTime);
              setField('entityMetadata.endTime', endTime);
              setField('displayData.startDateTime', parseISO(`${date} ${startTime}`));
              setField('displayData.endDateTime', parseISO(`${date} ${endTime}`));
            }}
            isWide
          />
        )}
      </Fieldset>
    );
  };

  renderScheduleName = ({ input: { value }, ruleEntityId }) => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
      <Message
        message={messages.label.schedule}
        as={FieldsetTitle}
        className="premium-caps-rule-modal__fieldset-title"
      />
      <p>
        {ruleEntityId} - {value}
      </p>
    </Fieldset>
  );

  formatDaysOfWeek = (value = []) =>
    value.reduce(
      (acc, day) => ({
        ...acc,
        [MAP_DAY_FULL_TO_ABBR[day]]: true,
      }),
      {}
    );

  parseDaysOfWeek = (value = {}) =>
    Object.entries(value).reduce(
      (acc, [abbr, isSelected]) => [...acc, ...(isSelected ? [MAP_DAY_ABBR_TO_FULL[abbr]] : [])],
      []
    );

  renderDaysOfWeek = props => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
      <Message
        message={messages.label.daysOfWeek}
        as={FieldsetTitle}
        className="premium-caps-rule-modal__fieldset-title"
      />
      <DayOfWeekPicker {...props} />
    </Fieldset>
  );

  formatDaysOfWeekForSchedule = value => {
    const { intl } = this.props;
    return !value ? '' : intl.formatDate(value, { format: 'dow-short' });
  };

  renderDaysOfWeekForSchedule = props => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
      <Message message={messages.label.day} as={FieldsetTitle} />
      <Input {...props} className="mb-0" placeholder="-" disabled />
    </Fieldset>
  );

  formatDate = value => (_isString(value) ? parseISO(value) : value);

  parseDate = value => (value instanceof Date ? format(value, 'yyyy-MM-dd') : value);

  renderDate = ({ names, labelMessage, disabled, attachment, ...fields }) => {
    const { intl } = this.props;
    const [dateField, ongoingField] = names.map(path => _get(fields, path, {}));
    const isOngoing = _get(ongoingField, 'input.value', false);

    return (
      <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
        <Message message={labelMessage} as={FieldsetTitle} />
        <DateControls
          date={dateField.input.value}
          setDate={dateField.input.onChange}
          intl={intl}
          disabled={disabled || isOngoing}
          attachment={attachment}
        />
        {ongoingField && this.renderCheckbox(ongoingField.input, commonMessages.ongoing, disabled)}
      </Fieldset>
    );
  };

  formatDateForSchedule = value => {
    const { intl } = this.props;
    return !value ? '' : intl.formatDate(value, { format: 'short' });
  };

  renderDateForSchedule = props => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
      <Message message={messages.label.endDate} as={FieldsetTitle} />
      <Input {...props} className="mb-0" placeholder="-" disabled />
    </Fieldset>
  );

  formatTime = (value = '') => value.split(':').slice(0, -1).join(':');

  parseTime = (value = '') => `${value}:00`;

  renderTime = ({ labelMessage, ...props }) => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
      <Message message={labelMessage} as={FieldsetTitle} />
      <TimePicker {...props} className="mb-0" />
    </Fieldset>
  );

  formatTimeForSchedule = value => {
    const { intl } = this.props;
    return !value ? '' : intl.formatDate(value, { format: 'time' });
  };

  renderTimeForSchedule = ({ labelMessage, ...props }) => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
      <Message message={labelMessage} as={FieldsetTitle} />
      <Input {...props} className="mb-0" placeholder="-" disabled />
    </Fieldset>
  );

  formatSpotCap = value => (value && _isString(value) ? parseInt(value, 10) : value);

  parseSpotCap = value => (value && _isString(value) ? parseInt(value, 10) : value);

  renderSpotCap = props => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/2">
      <Message message={messages.label.premiumCap} as={FieldsetTitle} />
      <Input {...props} className="mb-0" type="number" min="0" max="9000" />
    </Fieldset>
  );

  renderError = error => (
    <Fieldset className="premium-caps-rule-modal__fieldset grid__item 1/1">
      <Message message={error} as="p" className="premium-caps-rule-modal__error" />
    </Fieldset>
  );

  injectDeleteButton = buttons => {
    const { isEditModal, deletePremiumCapRule } = this.props;

    if (!isEditModal) {
      return buttons;
    }

    return React.Children.map(buttons, wrapper => {
      const children = React.Children.toArray(wrapper.props.children);
      return React.cloneElement(wrapper, {
        children: [
          <Button
            key="delete"
            type={Button.TYPES.BUTTON}
            className="premium-caps-rule-modal__delete-button confirm__button"
            styled="danger"
            onClick={deletePremiumCapRule}>
            <Message message={deleteModalMessages.accept} />
          </Button>,
          ...children,
        ],
      });
    });
  };

  render() {
    const {
      classes,
      schedules,
      fetchSchedulesForClass,
      currentValues,
      isEditModal,
      valid,
      dirty,
      error,
      submitting,
      handleSubmit,
    } = this.props;
    const {
      ruleEntity,
      ruleEntityId,
      ruleType,
      entityMetadata: { classId },
      displayData: { isVenueDefaultRule, isClassRule },
    } = currentValues;
    const modalTitleMessage = isEditModal ? messages.editTitle : messages.addTitle;
    const disableVenueFields =
      (isEditModal && ruleType === PREMIUM_CAP_RULE.VENUE_DEFAULT) || (!isEditModal && isVenueDefaultRule);
    const disableClassFields =
      (isEditModal && ruleType === PREMIUM_CAP_RULE.CLASS) || (!isEditModal && isClassRule) || !ruleEntityId;

    let fields = [];
    switch (ruleEntity) {
      case PREMIUM_CAP_ENTITY.VENUE:
        fields = [
          <Field
            key="displayData.isVenueDefaultRule"
            name="displayData.isVenueDefaultRule"
            component={this.renderRuleLevelCheckbox}
            labelMessage={commonMessages.venueDefault}
            disabled={isEditModal}
          />,
          <Field
            key="recurrence.daysOfWeek"
            name="recurrence.daysOfWeek"
            component={this.renderDaysOfWeek}
            format={this.formatDaysOfWeek}
            parse={this.parseDaysOfWeek}
            disabled={disableVenueFields}
          />,
          <Fields
            key="recurrence.dateStart"
            names={['recurrence.dateStart']}
            labelMessage={messages.label.startDate}
            component={this.renderDate}
            format={this.formatDate}
            parse={this.parseDate}
            disabled={disableVenueFields}
            attachment="left"
          />,
          <Fields
            key="dateEnd"
            names={['recurrence.dateEnd', 'displayData.isOngoing']}
            labelMessage={messages.label.endDate}
            component={this.renderDate}
            format={this.formatDate}
            parse={this.parseDate}
            disabled={disableVenueFields}
            attachment="right"
          />,
          <Field
            key="recurrence.timeOfDayStart"
            name="recurrence.timeOfDayStart"
            labelMessage={messages.label.startTime}
            component={this.renderTime}
            format={this.formatTime}
            parse={this.parseTime}
            disabled={disableVenueFields}
          />,
          <Field
            key="recurrence.timeOfDayEnd"
            name="recurrence.timeOfDayEnd"
            labelMessage={messages.label.endTime}
            component={this.renderTime}
            format={this.formatTime}
            parse={this.parseTime}
            disabled={disableVenueFields}
          />,
        ];
        break;

      case PREMIUM_CAP_ENTITY.CLASS:
        fields = [
          <Field
            key="ruleEntityId"
            name="ruleEntityId"
            component={this.renderClassId}
            disabled={isEditModal}
            classes={classes}
            fetchSchedulesForClass={fetchSchedulesForClass}
          />,
          <Field
            key="displayData.isClassRule"
            name="displayData.isClassRule"
            component={this.renderRuleLevelCheckbox}
            labelMessage={messages.label.allInstances}
            disabled={isEditModal}
          />,
          <Field
            key="recurrence.daysOfWeek"
            name="recurrence.daysOfWeek"
            component={this.renderDaysOfWeek}
            format={this.formatDaysOfWeek}
            parse={this.parseDaysOfWeek}
            disabled={disableClassFields}
          />,
          <Fields
            key="recurrence.dateStart"
            names={['recurrence.dateStart']}
            labelMessage={messages.label.startDate}
            component={this.renderDate}
            format={this.formatDate}
            parse={this.parseDate}
            disabled={disableClassFields}
            attachment="left"
          />,
          <Fields
            key="recurrence.dateEnd"
            names={['recurrence.dateEnd', 'displayData.isOngoing']}
            labelMessage={messages.label.endDate}
            component={this.renderDate}
            format={this.formatDate}
            parse={this.parseDate}
            disabled={disableClassFields}
            attachment="left"
          />,
          <Field
            key="recurrence.timeOfDayStart"
            name="recurrence.timeOfDayStart"
            labelMessage={messages.label.startTime}
            component={this.renderTime}
            format={this.formatTime}
            parse={this.parseTime}
            disabled={disableClassFields}
          />,
          <Field
            key="recurrence.timeOfDayEnd"
            name="recurrence.timeOfDayEnd"
            labelMessage={messages.label.endTime}
            component={this.renderTime}
            format={this.formatTime}
            parse={this.parseTime}
            disabled={disableClassFields}
          />,
        ];
        break;

      case PREMIUM_CAP_ENTITY.SCHEDULE:
      default:
        fields = [
          !isEditModal && (
            <Field
              key="entityMetadata.classId"
              name="entityMetadata.classId"
              component={this.renderClassId}
              classes={classes}
              fetchSchedulesForClass={fetchSchedulesForClass}
            />
          ),
          !isEditModal ? (
            <Field
              key="ruleEntityId"
              name="ruleEntityId"
              component={this.renderScheduleId}
              classId={classId}
              schedules={schedules}
            />
          ) : (
            <Field
              key="entityMetadata.name"
              name="entityMetadata.name"
              component={this.renderScheduleName}
              ruleEntityId={ruleEntityId}
            />
          ),
          <Field
            key="displayData.startDateTime-day"
            name="displayData.startDateTime"
            component={this.renderDaysOfWeekForSchedule}
            format={this.formatDaysOfWeekForSchedule}
          />,
          <Field
            key="displayData.startDateTime-date"
            name="displayData.startDateTime"
            component={this.renderDateForSchedule}
            format={this.formatDateForSchedule}
          />,
          <Field
            key="displayData.startDateTime-start-time"
            name="displayData.startDateTime"
            labelMessage={messages.label.startTime}
            component={this.renderTimeForSchedule}
            format={this.formatTimeForSchedule}
          />,
          <Field
            key="displayData.endDateTime"
            name="displayData.endDateTime"
            labelMessage={messages.label.endTime}
            component={this.renderTimeForSchedule}
            format={this.formatTimeForSchedule}
          />,
        ];
        break;
    }

    return (
      <Confirm
        title={<Message message={modalTitleMessage} />}
        cancelNode={<Message message={messages.cancel} />}
        acceptNode={<Message message={messages.accept} />}
        acceptDisabled={!dirty || !valid || submitting}
        acceptWaiting={submitting}
        onConfirm={handleSubmit}
        isOpen
        size="extended">
        {({ title, buttons, bodyClass }) => (
          <div className="premium-caps-rule-modal">
            <Banner id="premium-caps-rule-modal" />
            {title}
            <div className={classNames('premium-caps-rule-modal__content grid', bodyClass)}>
              <Field name="ruleEntityId" component={this.renderRuleEntity} disabled={isEditModal} />
              {fields.map(component => component)}
              <Field
                name="spotCap"
                component={this.renderSpotCap}
                format={this.formatSpotCap}
                parse={this.parseSpotCap}
              />
              {error && this.renderError(error)}
            </div>
            {this.injectDeleteButton(buttons)}
          </div>
        )}
      </Confirm>
    );
  }
}
